import React, { FC, ReactNode, useRef, useState } from 'react';
import cx from 'classnames';
import { Membership } from 't2-graphql-types';
import { useT2Query } from '../../hooks/useT2Query';
import Navbar from '../../components/Navbar';
import { TerritoryTabContainer } from '../../components/TerritoryTabSection/TerritoryTabContainer';
import { TerritorySidebar } from '../../components/TerritorySidebar/TerritorySidebar';
import TerritoryTabProvider from '../../components/TerritoryTabSection/context/territoryTab';
import { TerritorySelectedTab } from '../../components/TerritoryTabSection/constants';
import { TerritoryDetailsModalComponent } from '../../components/TerritoryDetailsModalComponent/TerritoryDetailsModalComponent';
import { TerritoryDetailsModalContextProvider } from '../../components/TerritoryDetails/context/TerritoryDetailsModalContextProvider';
import { getTerritoryHeaderDataQuery } from '../../components/TerritoryHeader/queries/getTerritoryHeaderDataQuery';
import { useUserTerritoryPermissions } from '../../components/Territory/services/useUserTerritoryPermissions';
import { TerritoryDetails } from '../../components/TerritoryDetails/TerritoryDetails';
import { TerritoryHomePageContextProvider } from '../../components/TerritoryHomePageContext/TerritoryHomePageContextProvider';
import TerritoryFilterProvider from '../../components/TerritoryFilter/context/territoryFilter';
import { TerritoryPageExploreFilterLayout } from './TerritoryPageExploreFilterLayout';
import { TerritoryActiveMainFilter } from '../../components/TerritoryFilter/constants';
import { TerritoryTabStickyWrapper } from '../../components/TerritoryTabSection/TerritoryTabStickyWrapper';
import useDetectScrollDirection from '../../components/DiscordButton/hooks/useDetectScrollDirection';
import { useNavbarHeight } from '../../components/Navbar/hooks/useNavbarHeight';
import { useAnalytics } from '../../components/AnalyticsProvider/AnalyticsProvider';

export enum TerritoryVersionType {
  V1 = 'v1',
  V2 = 'v2',
}

type TTerritoryPageLayoutProps = {
  children: ReactNode;
  territoryName: string;
  initialTerritoryTab?: TerritorySelectedTab;
  territoryId: string;
  territoryVersion: TerritoryVersionType;
  territorySlug: string;
  initialTerritoryExploreFilter?: TerritoryActiveMainFilter;
  showExplorePageFilters?: boolean;
};

export const TerritoryPageLayout: FC<TTerritoryPageLayoutProps> = ({
  children,
  territoryName,
  initialTerritoryTab,
  territoryId,
  territoryVersion,
  territorySlug,
  initialTerritoryExploreFilter,
  showExplorePageFilters = false,
}) => {
  const territoryPageRef = useRef(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const navbarHeight = useNavbarHeight();
  const scrollDirection = useDetectScrollDirection();
  const { sendEvent } = useAnalytics();

  const { data: territoryHeaderData, loading: isTerritoryHeaderLoading } = useT2Query(getTerritoryHeaderDataQuery, {
    variables: {
      territoryId,
    },
    returnPartialData: true,
  });

  const handleSidebarClick = () => {
    setIsSidebarOpen(!isSidebarOpen);
    sendEvent('territory_page_sidebar_click');
  };

  const territory = territoryHeaderData?.territory;

  const userTerritoryPermissions = useUserTerritoryPermissions({
    territoryId: territory?.id,
  });

  const territoryMembers = (territory?.memberships || []).map((m: Membership) => m.user);
  const territoryMembersCount = territory?.membershipsCount;

  const renderContent = () => {
    if (showExplorePageFilters) {
      return (
        <TerritoryFilterProvider initialTerritoryExploreFilter={initialTerritoryExploreFilter}>
          <TerritoryTabStickyWrapper navbarHeight={navbarHeight} scrollDirection={scrollDirection}>
            <TerritoryTabContainer
              onClick={handleSidebarClick}
              isSidebarOpen={isSidebarOpen}
              territoryName={territoryName}
              territoryVersion={territoryVersion}
            />
            <TerritoryPageExploreFilterLayout
              territoryId={territoryId}
              territorySlug={territorySlug}
              territoryVersion={territoryVersion}
              territoryPageRef={territoryPageRef}
            />
          </TerritoryTabStickyWrapper>
          <div className="mt-5">{children}</div>
        </TerritoryFilterProvider>
      );
    }

    return (
      <>
        <TerritoryTabStickyWrapper navbarHeight={navbarHeight} scrollDirection={scrollDirection}>
          <TerritoryTabContainer
            onClick={handleSidebarClick}
            isSidebarOpen={isSidebarOpen}
            territoryName={territoryName}
            territoryVersion={territoryVersion}
          />
        </TerritoryTabStickyWrapper>
        <div className="mt-5">{children}</div>
      </>
    );
  };

  return (
    <TerritoryHomePageContextProvider
      territorySlug={territorySlug}
      territoryId={territoryId}
      territoryVersion={territoryVersion}
    >
      <Navbar />
      <TerritoryDetailsModalContextProvider>
        <div className="min-h-fitScreen bg-white-100 relative">
          <div className="flex min-h-fitScreen max-w-[1440px] mx-auto mt-24 md:mt-32 px-4 lg:px-8">
            <TerritorySidebar isOpen={isSidebarOpen}>
              <div className={cx('hidden md:block', { 'md:hidden': !isSidebarOpen })}>
                <TerritoryDetails
                  territory={territory}
                  territoryMembers={territoryMembers}
                  territoryMembersCount={territoryMembersCount}
                  territoryHeaderLoading={isTerritoryHeaderLoading}
                />
              </div>
            </TerritorySidebar>
            <div
              ref={territoryPageRef}
              className={cx('w-full md:ml-5 bg-t2-neutral-grey01 ', {
                '!ml-0': !isSidebarOpen,
              })}
            >
              <div className="block relative md:hidden">
                <TerritoryDetails
                  territory={territory}
                  territoryMembers={territoryMembers}
                  territoryMembersCount={territoryMembersCount}
                  territoryHeaderLoading={isTerritoryHeaderLoading}
                />
              </div>
              <TerritoryTabProvider initialTerritoryTab={initialTerritoryTab}>{renderContent()}</TerritoryTabProvider>
            </div>
          </div>
        </div>
        <TerritoryDetailsModalComponent
          territory={territory}
          userTerritoryPermissions={userTerritoryPermissions}
          territoryMembers={territoryMembers}
          territoryMembersCount={territoryMembersCount}
        />
      </TerritoryDetailsModalContextProvider>
    </TerritoryHomePageContextProvider>
  );
};
